import classNames from 'classnames'
import { useMemo, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { createFilter } from 'react-select'

import type { ChangeEvent, ReactElement } from 'react'
import type { IApp } from '~/types/iap'
import type { IAppOption } from '~/types/option'

import AppLogo from '~/components/AppLogo'
import Button from '~/components/Buttons/Button'
import IapCard from '~/components/IapCard'
import MultiSelectInput from '~/components/Inputs/Basic/MultiSelectInput'
import SearchInput from '~/components/Inputs/Basic/SearchInput'
import Layout from '~/components/Layouts/Layout'
import MwMLoader from '~/components/MwMLoader'
import Section from '~/components/Section'
import CustomAppOption from '~/components/SelectOptions/CustomAppOption'
import ViewTitle from '~/components/ViewTitle'
import { CATEGORY_URLS } from '~/const/category-urls'
import useApps from '~/hooks/useApps'
import useDocumentTitle from '~/hooks/useDocumentTitle'
import { useIapContext } from '~/providers/Iap'
import { useSearchParamsContext } from '~/providers/SearchParams'
import { useFeaturesIdsListQuery, useOffersListQuery, useSkusIdsListQuery } from '~/queries/iap'
import { appKeyDeviceTypeToString } from '~/utils/application'

import './styles.scss'

type TFilterKey = 'apps_origin' | 'apps_unlocked' | 'features' | 'skus'

const formatAppsMultiSelectInputValue = (value: IApp[], options: IAppOption[]) =>
  value.map((app) => options.find((el) => app.app_key === el.appKey && app.device_type === el.deviceType))

const OffersView = (): ReactElement => {
  /** Local state */

  const navigate = useNavigate()

  const { dispatch, state } = useIapContext()

  const { apps, appsUnlocked, features, skus } = state

  const { dispatchToSearchParams } = useSearchParamsContext()

  const [search, setSearch] = useState('')

  /** Queries and Mutations */

  const { data: offers, isLoading: isOffersLoading } = useOffersListQuery({
    appsInfos: {
      apps_origin: apps,
      apps_unlocked: appsUnlocked,
      features,
      skus,
    },
    limit: 100,
    page: 0,
    searchQuery: search,
  })

  const { appsOptions, isAppsLoading } = useApps()

  const formattedAppsOptions: IAppOption[] = appsOptions.map((el) => ({
    ...el,
    label: `${el.label} - ${el.deviceType}`,
    value: appKeyDeviceTypeToString(el.appKey, el.deviceType),
  }))

  const { data: featureIds, isLoading: isFeaturesIdsLoading } = useFeaturesIdsListQuery()

  const { data: skusIds, isLoading: isSkusIdsLoading } = useSkusIdsListQuery()

  /** Query dependent state */

  const duplicatesFreeFeatureIds = useMemo(() => [...new Set(featureIds)], [featureIds])

  const inAppsFilterOptions: IOption[] = useMemo(
    () => skusIds?.map((sku) => ({ label: sku, value: sku })) || [],
    [skusIds]
  )

  const featuresFilterOptions: IOption[] = useMemo(
    () => duplicatesFreeFeatureIds.map((id) => ({ label: id, value: id })) || [],
    [duplicatesFreeFeatureIds]
  )

  /** Effects */

  useDocumentTitle('IAP - Offers')

  /** Handlers */

  const handleCreateOrEditOfferClick = (id?: string) =>
    navigate(`${CATEGORY_URLS.IN_APP_PURCHASES}/offers/editor${id ? `/${id}` : ''}`)

  const handleSearchChange = (evt: ChangeEvent<HTMLInputElement>) => setSearch(evt.target.value)

  const handleFilterChange = (field: TFilterKey, value: IAppOption[] | IOption[]) => {
    switch (field) {
      case 'apps_origin':
        dispatch({
          type: 'apps',
          value: (value as IAppOption[]).map(
            ({ appKey, deviceType }): IApp => ({ app_key: appKey, device_type: deviceType })
          ),
        })
        dispatchToSearchParams({
          type: 'update',
          value: { apps: value.map(({ value }) => value) },
        })
        break
      case 'apps_unlocked':
        dispatch({
          type: 'appsUnlocked',
          value: (value as IAppOption[]).map(
            ({ appKey, deviceType }): IApp => ({ app_key: appKey, device_type: deviceType })
          ),
        })
        dispatchToSearchParams({
          type: 'update',
          value: { apps: value.map(({ value }) => value) },
        })
        break
      default:
        dispatch({ type: field, value: value.map(({ value }) => value) })
        dispatchToSearchParams({
          type: 'update',
          value: { [field]: value.map(({ value }) => value) },
        })
    }
  }

  const handleResetFiltersClick = () => {
    dispatch({ type: 'reset' })
    dispatchToSearchParams({ type: 'reset-iap-filters' })
  }

  /** Render */

  return (
    <Layout className="in-apps-offers-view" withPadding>
      <ViewTitle>
        <span>In-Apps Offers</span>
      </ViewTitle>
      <div className="in-apps-offers-view__content-layout">
        <Section className="in-apps-offers-view__content-layout__left-section">
          <div className="in-apps-offers-view__content-layout__left-section__filters">
            <MultiSelectInput
              className="multi-select-filters"
              components={{ Option: CustomAppOption }}
              isLoading={isAppsLoading}
              label="Apps origin"
              name="apps_origin"
              onChange={(options) => handleFilterChange('apps_origin', options as IAppOption[])}
              options={formattedAppsOptions}
              value={formatAppsMultiSelectInputValue(apps, formattedAppsOptions)}
            />
            <MultiSelectInput
              className="multi-select-filters"
              components={{ Option: CustomAppOption }}
              isLoading={isAppsLoading}
              label="Apps unlocked"
              name="apps_unlocked"
              onChange={(options) => handleFilterChange('apps_unlocked', options as IAppOption[])}
              options={formattedAppsOptions}
              value={formatAppsMultiSelectInputValue(appsUnlocked, formattedAppsOptions)}
            />
            <MultiSelectInput
              className="multi-select-filters"
              filterOption={createFilter({ ignoreAccents: false })}
              isLoading={isSkusIdsLoading}
              label="In-apps"
              name="in-apps"
              onChange={(options) => handleFilterChange('skus', options as IOption[])}
              options={inAppsFilterOptions}
              value={inAppsFilterOptions.filter(({ value }) => skus.includes(value))}
            />
            <MultiSelectInput
              className="multi-select-filters"
              filterOption={createFilter({ ignoreAccents: false })}
              isLoading={isFeaturesIdsLoading}
              label="Features unlocked"
              name="features"
              onChange={(options) => handleFilterChange('features', options as IOption[])}
              options={featuresFilterOptions}
              value={featuresFilterOptions.filter(({ value }) => features.includes(value))}
            />
            <Button className="reset-filters-button" onClick={handleResetFiltersClick} size="small" type="reset">
              Reset
            </Button>
          </div>
        </Section>
        <Section className="in-apps-offers-view__content-layout__right-section">
          <div className="in-apps-offers-view__content-layout__right-section__actions">
            <SearchInput className="search-input" onChange={handleSearchChange} />
            <Button onClick={() => handleCreateOrEditOfferClick()}>Create new offer</Button>
          </div>
          {!isOffersLoading ? (
            offers && offers.data && offers.data.length > 0 ? (
              <div className="in-apps-offers-view__content-layout__right-section__content">
                {offers?.data?.map(
                  ({ available_in_dev, available_in_prod, features_groups, in_apps, name, offer_id }, idx) => {
                    return (
                      <IapCard
                        className="iap-card"
                        content={
                          <div className="iap-card__content">
                            {in_apps.length > 0 ? (
                              <div className="iap-card__content__iaps">
                                <span>{in_apps.flatMap(({ skus }) => skus).length}</span> IAPs (
                                <div className="iap-card__content__iaps__apps">
                                  {in_apps
                                    .map(({ app_key, device_type, skus }, idx) => {
                                      const appLogoUrl = appsOptions.find(({ appKey }) => appKey === app_key)?.url

                                      return (
                                        <>
                                          <AppLogo
                                            appLogoUrl={appLogoUrl}
                                            appPlatform={device_type}
                                            key={`${app_key}-${device_type}-${idx}`}
                                            size="small"
                                          />
                                          <div>{skus.length}</div>
                                        </>
                                      )
                                    })
                                    .slice(0, 5)}
                                </div>
                                )
                              </div>
                            ) : null}
                            {features_groups.length > 0 ? (
                              <div className="iap-card__content__iaps__features">
                                unlock <span>{features_groups.length}</span> features groups (
                                {features_groups.map(({ app_key, device_type, name }, idx) => {
                                  const appLogoUrl = appsOptions.find(({ appKey }) => appKey === app_key)?.url

                                  return (
                                    <div
                                      className={classNames('iap-feature-badge', device_type)}
                                      key={`${app_key}-${device_type}-${name}-${idx}`}
                                    >
                                      <AppLogo appLogoUrl={appLogoUrl} appPlatform={device_type} size="small" />
                                      <div>{name}</div>
                                    </div>
                                  )
                                })}
                                )
                              </div>
                            ) : null}
                            <div className="iap-card__content__iaps__environment">
                              in <div className={classNames('iap-env-badge', { prod: available_in_prod })}>Prod</div>{' '}
                              <div className={classNames('iap-env-badge', { dev: available_in_dev })}>Dev</div>
                            </div>
                          </div>
                        }
                        key={`${offer_id}-${idx}`}
                        onEditClick={() => handleCreateOrEditOfferClick(offer_id)}
                        titleComponent={<>{name}</>}
                        type="edit"
                      />
                    )
                  }
                )}
              </div>
            ) : (
              <div className="in-apps-offers-view__content-layout__right-section__no-data">Sorry, no offer found</div>
            )
          ) : (
            <MwMLoader category="general" />
          )}
        </Section>
      </div>
    </Layout>
  )
}

export default OffersView
